/**
 * AppearanceHub Component
 * 
 * Unified settings hub for Display Mode (Light/Dark/System), Theme selection,
 * and contextual Layout preferences. Displays as a glassmorphism popover on desktop.
 * 
 * Features:
 * - Display mode: Light/Dark/System toggle (System default for new users)
 * - Theme selection: Refactored from ThemePicker, compact grid format
 * - Layout selection: Contextual based on current page type
 * - Keyboard shortcuts: , (comma) to open, Alt+D for dark mode toggle, ESC to close
 * - Click outside to close
 * - Spring physics animations
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { setTheme, setLayoutPreference } from '../../slices/pageLayoutSettingsSlice.mjs';
import useCurrentPageType from '../../hooks/useCurrentPageType.mjs';
import { setGuestLayoutPreferences } from '../../utils/layoutPreferencesStorage.mjs';

// =============================================================================
// Constants
// =============================================================================

const DISPLAY_MODES = [
    { id: 'light', icon: '☀️', label: 'Light' },
    { id: 'dark', icon: '🌙', label: 'Dark' },
    { id: 'system', icon: '🖥️', label: 'System' }
];

const AVAILABLE_THEMES = [
    { name: 'default', label: 'Default', icon: '🌐' },
    { name: 'court-side', label: 'Court Side', icon: '🏀' },
    { name: 'full-court-press', label: 'Full Court', icon: '⚡' },
    { name: 'hardwood', label: 'Hardwood', icon: '🌳' },
    { name: 'triple-double', label: 'Triple', icon: '🏆' },
    { name: 'downtown', label: 'Downtown', icon: '🌃' },
    { name: 'buzzer-beater', label: 'Buzzer', icon: '⏱️' }
];

const LAYOUTS_BY_TYPE = {
    homepage: [
        { id: 'default', name: 'Default', description: 'Standard card grid', icon: '📰' },
        { id: 'topic-focus', name: 'Topic Focus', description: 'Grouped by topic', icon: '🏷️' },
        { id: 'author-spotlight', name: 'Author Spotlight', description: 'Featured writers', icon: '✍️' },
        { id: 'long-form', name: 'Long Form', description: 'Magazine style', icon: '📖' }
    ],
    categoryPage: [
        { id: 'default', name: 'Default', description: 'Standard grid', icon: '📋' },
        { id: 'topic-grid', name: 'Topic Grid', description: 'Organized by topic', icon: '🗂️' },
        { id: 'timeline', name: 'Timeline', description: 'Chronological', icon: '📅' }
    ],
    articleDetail: [
        { id: 'default', name: 'Default', description: 'Standard reading', icon: '📄' },
        { id: 'immersive', name: 'Immersive', description: 'Full-width hero', icon: '🎬' },
        { id: 'analytical', name: 'Analytical', description: 'Facts sidebar', icon: '📊' },
        { id: 'collaborative', name: 'Collaborative', description: 'Contributor voices', icon: '👥' }
    ]
};

// =============================================================================
// Display Mode Section
// =============================================================================

const DisplayModeSection = ({ currentMode, onModeChange }) => {
    return (
        <div className="appearance-hub-section">
            <div className="appearance-hub-section-title">
                <span>Display Mode</span>
                <span className="keyboard-shortcut">Alt+D</span>
            </div>
            <div className="display-mode-control">
                {DISPLAY_MODES.map(mode => (
                    <button
                        key={mode.id}
                        className={`display-mode-option ${currentMode === mode.id ? 'selected' : ''}`}
                        onClick={() => onModeChange(mode.id)}
                        aria-pressed={currentMode === mode.id}
                    >
                        <span className="icon">{mode.icon}</span>
                        <span className="label">{mode.label}</span>
                    </button>
                ))}
            </div>
        </div>
    );
};

// =============================================================================
// Theme Section
// =============================================================================

const ThemeSection = ({ currentTheme, onThemeChange }) => {
    return (
        <div className="appearance-hub-section">
            <div className="appearance-hub-section-title">
                <span>Theme</span>
            </div>
            <div className="theme-grid">
                {AVAILABLE_THEMES.map(theme => (
                    <button
                        key={theme.name}
                        className={`theme-option ${currentTheme === theme.name ? 'selected' : ''}`}
                        onClick={() => onThemeChange(theme)}
                        aria-pressed={currentTheme === theme.name}
                        title={theme.label}
                    >
                        <span className="icon">{theme.icon}</span>
                        <span className="label">{theme.label}</span>
                    </button>
                ))}
            </div>
        </div>
    );
};

// =============================================================================
// Layout Section
// =============================================================================

const LayoutSection = ({ pageType, pageTypeLabel, currentLayout, onLayoutChange, layouts }) => {
    if (!pageType || !layouts || layouts.length === 0) {
        return null;
    }

    return (
        <div className="appearance-hub-section">
            <div className="appearance-hub-section-title">
                <span>{pageTypeLabel} Layout</span>
            </div>
            <div className="layout-grid">
                {layouts.map(layout => (
                    <button
                        key={layout.id}
                        className={`layout-option ${currentLayout === layout.id ? 'selected' : ''}`}
                        onClick={() => onLayoutChange(layout.id)}
                        aria-pressed={currentLayout === layout.id}
                    >
                        <span className="icon">{layout.icon}</span>
                        <span className="name">{layout.name}</span>
                        <span className="description">{layout.description}</span>
                    </button>
                ))}
            </div>
        </div>
    );
};

// =============================================================================
// Main AppearanceHub Component
// =============================================================================

export default function AppearanceHub({ className = '' }) {
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);
    // Read displayMode synchronously from localStorage to prevent flash.
    // Without this, useState('system') → useEffect toggles dark class → localStorage read
    // updates to 'light' → useEffect removes dark class = 1-frame dark flash.
    const [displayMode, setDisplayMode] = useState(() => {
        if (typeof window === 'undefined') return 'system';
        const saved = localStorage.getItem('displayMode');
        if (saved && ['light', 'dark', 'system'].includes(saved)) return saved;
        localStorage.setItem('displayMode', 'system');
        return 'system';
    });
    const popoverRef = useRef(null);
    const triggerRef = useRef(null);

    // Redux state
    const resolvedTheme = useSelector(state => state.pageLayoutSettings?.resolvedTheme);
    const currentThemeName = resolvedTheme?.name || 'default';
    const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);
    const userLayoutPreferences = useSelector(state => state.pageLayoutSettings?.userLayoutPreferences || {});

    // Current page type for contextual layouts
    const { pageType, label: pageTypeLabel } = useCurrentPageType();
    const layouts = pageType ? LAYOUTS_BY_TYPE[pageType] : [];
    const currentLayout = userLayoutPreferences[pageType] || 'default';

    // Apply display mode to document
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const applyMode = (mode) => {
            const html = document.documentElement;

            if (mode === 'system') {
                const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                html.classList.toggle('dark', prefersDark);
            } else {
                html.classList.toggle('dark', mode === 'dark');
            }
        };

        applyMode(displayMode);

        // Listen for system preference changes when in system mode
        if (displayMode === 'system') {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handleChange = () => applyMode('system');
            mediaQuery.addEventListener('change', handleChange);
            return () => mediaQuery.removeEventListener('change', handleChange);
        }
    }, [displayMode]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(event.target) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Comma to toggle popover
            if (event.key === ',' && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
                // Don't trigger if user is typing in an input
                if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
                    return;
                }
                event.preventDefault();
                setIsOpen(prev => !prev);
            }

            // ESC to close
            if (event.key === 'Escape' && isOpen) {
                setIsOpen(false);
            }

            // Alt+D to toggle dark mode (quick toggle, skips system)
            if (event.key === 'd' && event.altKey && !event.ctrlKey && !event.metaKey) {
                event.preventDefault();
                handleDisplayModeChange(displayMode === 'dark' ? 'light' : 'dark');
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, displayMode]);

    // Handlers
    const handleDisplayModeChange = useCallback(async (mode) => {
        setDisplayMode(mode);
        localStorage.setItem('displayMode', mode);

        // Persist to database for authenticated users
        if (isAuthenticated) {
            try {
                // Map mode to colorScheme for API
                let colorScheme = mode;
                if (mode === 'system') {
                    colorScheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
                }
                await axios.patch('/q/api/user-preferences', { colorScheme, displayMode: mode });
            } catch (error) {
                console.warn('Failed to persist display mode preference:', error.message);
            }
        }
    }, [isAuthenticated]);

    const handleThemeChange = useCallback(async (theme) => {
        // Update Redux state
        dispatch(setTheme({ name: theme.name, version: null }));

        // Apply theme to HTML element immediately
        if (typeof document !== 'undefined') {
            const html = document.documentElement;
            if (theme.name === 'default') {
                html.removeAttribute('data-theme');
            } else {
                html.setAttribute('data-theme', theme.name);
            }
        }

        // Persist to database for authenticated users
        if (isAuthenticated) {
            try {
                await axios.patch('/q/api/user-preferences', {
                    selectedTheme: theme.name === 'default' ? null : theme.name
                });
            } catch (error) {
                console.warn('Failed to persist theme preference:', error.message);
            }
        }

        // Save to localStorage as fallback
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem('selectedTheme', theme.name);
        }
    }, [dispatch, isAuthenticated]);

    const handleLayoutChange = useCallback(async (layoutId) => {
        if (!pageType) return;

        // Update Redux state
        dispatch(setLayoutPreference({ pageType, layout: layoutId }));

        // Persist
        if (isAuthenticated) {
            try {
                await axios.patch('/q/api/user-preferences', {
                    layouts: { [pageType]: layoutId }
                });
            } catch (error) {
                console.warn('Failed to persist layout preference:', error.message);
            }
        } else {
            // Guest: save to localStorage
            setGuestLayoutPreferences({ [pageType]: layoutId });
        }
    }, [dispatch, isAuthenticated, pageType]);

    return (
        <div className={`appearance-hub relative ${className}`}>
            {/* Trigger Button */}
            <button
                ref={triggerRef}
                className="appearance-hub-trigger"
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                aria-haspopup="dialog"
                aria-label="Appearance and layout settings"
                title="Appearance & Layout (,)"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"
                    />
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                    />
                </svg>
            </button>

            {/* Popover */}
            {isOpen && (
                <div
                    ref={popoverRef}
                    className="appearance-hub-popover"
                    role="dialog"
                    aria-label="Appearance settings"
                >
                    <DisplayModeSection
                        currentMode={displayMode}
                        onModeChange={handleDisplayModeChange}
                    />

                    <div className="appearance-hub-divider" />

                    <ThemeSection
                        currentTheme={currentThemeName}
                        onThemeChange={handleThemeChange}
                    />

                    {pageType && layouts.length > 0 && (
                        <>
                            <div className="appearance-hub-divider" />
                            <LayoutSection
                                pageType={pageType}
                                pageTypeLabel={pageTypeLabel}
                                currentLayout={currentLayout}
                                onLayoutChange={handleLayoutChange}
                                layouts={layouts}
                            />
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// Export for mobile menu usage
export {
    DISPLAY_MODES,
    AVAILABLE_THEMES,
    LAYOUTS_BY_TYPE
};
